import React, { memo, useEffect, useMemo, useState } from "react";
import { FormGroup, Input, Label, Row } from "reactstrap";
import { OroLoaderInline } from "../../core";
import ObBondsNotFound from "./ObBondsNotFound";
import ObDisclaimer from "./ObDisclaimer";
import ObOpportunityBase from "./ObOpportunityBase";
import ObOpportunityComingSoon from "./ObOpportunityComingSoon";
import { referralBanner } from "../services/waitlist-service";
import { toast } from "react-toastify";
import PopUpReferral from "./PopUpReferral";
import Slider from "react-slick";
import StackGrid, { transitions } from "react-stack-grid";
import useCardWidth from "../hooks/gridWidth";

const ObOpportunitySection = memo(function ObOpportunitySection(props) {
  // console.log(">> ObOpportunitySection");
  const [selectedFilter, setSelectedFilter] = useState('');
  const [sortedCards, setSortedCards] = useState()
  const [referralsDetail, setReferralsDetail] = useState()
  const [popUpReferral, setPopUpReferral] = useState(false)
  const cardWidth = useCardWidth();
  const { scaleDown } = transitions;

  const settings = {
    dots: false,
    infinite: true,
    autoplay: false,
    speed: 500,
    slidesToShow: props?.isMobile ? 1 : 2,
    slidesToScroll: 1,
    variableWidth: true,
    rows: 1
  };

  useEffect(() => {
    // Default selection logic based on priority
    // 1. Check for live bonds first
    if (props.opportunities.find(x => x && x.productType === "bonds" && x.showOnBrowse && x.isActive && x.category === "live")) {
      setSelectedFilter("bonds");
      return;
    }
    // 2. If no live bonds, check for live MLDs
    if (props.opportunities.find(x => x && x.productType === "mld" && x.showOnBrowse && x.isActive && x.category === "live")) {
      setSelectedFilter("mld");
      return;
    }
    // 3. If no live MLDs, check for upcoming
    if (props.opportunities.find(x => x && x.showOnBrowse && x.isActive && x.category === "upcoming")) {
      setSelectedFilter("upcoming");
      return;
    }
    // 4. Finally check for historical
    if (props.opportunities.find(x => x && x.showOnBrowse && x.isActive && x.category === "historical")) {
      setSelectedFilter("historical");
      return;
    }
    // Default to bonds if nothing found
    setSelectedFilter("bonds");
  }, [props.opportunities])

  const { isMobile } = props;

  const handleFilterChange = (filterType) => {
    setSelectedFilter(filterType);
  }

  const filteredValue = useMemo(() => {
    let filtered = [];

    switch(selectedFilter) {
      case 'bonds':
        // Show live bonds only
        filtered = props.opportunities.filter(item => 
          item.isActive && 
          item.showOnBrowse && 
          item.productType === 'bonds' &&
          item.category === 'live'
        );
        break;
      
      case 'mld':
        // Show live MLDs only
        filtered = props.opportunities.filter(item => 
          item.isActive && 
          item.showOnBrowse && 
          item.productType === 'mld' &&
          item.category === 'live'
        );
        break;
      
      case 'upcoming':
        // Show upcoming opportunities (any product type)
        filtered = props.opportunities.filter(item => 
          item.isActive && 
          item.showOnBrowse && 
          item.category === 'upcoming'
        );
        break;
      
      case 'historical':
        // Show historical opportunities (any product type)
        filtered = props.opportunities.filter(item => 
          item.isActive && 
          item.showOnBrowse && 
          item.category === 'historical'
        );
        break;
      
      default:
        filtered = [];
    }

    return filtered;
  }, [selectedFilter, props.opportunities])

  useEffect(() => {
    let tempSortedCards = [...filteredValue].sort((a, b) => {
      let orderA = a.order || null;
      let orderB = b.order || null;
      return orderA - orderB
    })
    setSortedCards(tempSortedCards)
  }, [filteredValue])

  const referralPopUpDetails = () => {
    let temSession = sessionStorage.getItem("loggedInFirst");

    if (!temSession) {
      referralBanner().then((response) => {
        sessionStorage.setItem("loggedInFirst", "true"); // Prevents further calls
        if (response && response.data) {
          let tempReferralDetails = response.data.filter((i) => i.type === "popup" && i.show);

          // Only execute if `tempReferralDetails` has valid data
          if (tempReferralDetails.length > 0) {
            setPopUpReferral(true);
            setReferralsDetail(tempReferralDetails[0]);
          }
        }
      });
    }
  };

  const toggleReferalPopUp = () => {
    setPopUpReferral(!popUpReferral)
  }

  useEffect(() => {
    const timer = setTimeout(() => {
      referralPopUpDetails();
    }, 1000);
    return () => clearTimeout(timer);
  }, [])

  return (
    !props.oppLoader ? <>
      <div className={"header-container w-100 " + (!isMobile ? 'd-flex' : 'flex-column')}>
        <p className={"font-weight-semi-bold header mb-0 " + (isMobile ? 'pl-2' : '')}>
          Investment Opportunities
        </p>
        <div className="filter-content">
          {/* MLD Filter */}
          <FormGroup className={"radio-item mb-0 mr-2" + (selectedFilter === 'mld' ? ' selected' : '')}>
            <Input 
              type="radio" 
              checked={selectedFilter === 'mld'} 
              onChange={() => handleFilterChange('mld')} 
            />
            <Label className="mb-0" onClick={() => handleFilterChange('mld')}>MLD</Label>
          </FormGroup>

          {/* Bonds Filter */}
          <FormGroup className={"radio-item mb-0 mr-2" + (selectedFilter === 'bonds' ? ' selected' : '')}>
            <Input 
              type="radio" 
              checked={selectedFilter === 'bonds'} 
              onChange={() => handleFilterChange('bonds')} 
            />
            <Label className="mb-0" onClick={() => handleFilterChange('bonds')}>Bonds</Label>
          </FormGroup>

          {/* Upcoming Filter */}
          <FormGroup className={"radio-item mb-0 mr-2" + (selectedFilter === 'upcoming' ? ' selected' : '')}>
            <Input 
              type="radio" 
              checked={selectedFilter === 'upcoming'} 
              onChange={() => handleFilterChange('upcoming')} 
            />
            <Label className="mb-0" onClick={() => handleFilterChange('upcoming')}>Upcoming</Label>
          </FormGroup>

          {/* Historical Filter */}
          <FormGroup className={"radio-item mb-0" + (selectedFilter === 'historical' ? ' selected' : '')}>
            <Input 
              type="radio" 
              checked={selectedFilter === 'historical'} 
              onChange={() => handleFilterChange('historical')} 
            />
            <Label className="mb-0" onClick={() => handleFilterChange('historical')}>Historical</Label>
          </FormGroup>
        </div>
      </div>
      
      <StackGrid className="opp-section-main" 
        columnWidth={cardWidth} 
        gutterWidth={25} 
        gutterHeight={25} 
        appear={scaleDown.fade}
      >
        {sortedCards && sortedCards.length ? (
          sortedCards.map((item) => (
            item.showOnBrowse && item.isActive ?
              (
                <ObOpportunityBase
                  opportunity={item}
                  isMobile={isMobile}
                  userEmail={props && props.userEmail ? props.userEmail : ""}
                  utm={props.utm}
                />
              )
              : (
                selectedFilter === 'upcoming'
                  ? <ObOpportunityComingSoon />
                  : <ObBondsNotFound />
              )
          ))
        ) : (
          selectedFilter === 'upcoming'
            ? <ObOpportunityComingSoon />
            : <ObBondsNotFound />
        )}
      </StackGrid>
      
      <PopUpReferral
        showPopUpText={popUpReferral}
        toggleInfoPopup={toggleReferalPopUp}
        isMobile={isMobile}
        title={referralsDetail?.content.title}
        imageUrl={referralsDetail?.content.imageUrl}
        description={referralsDetail?.content.description}
        videoUrl={referralsDetail?.content.videoUrl}
      />
      <ObDisclaimer isMobile={isMobile}/>
    </> : <OroLoaderInline />
  );
});

export default ObOpportunitySection;